import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getPageIdForSelectedApp } from "../config/uiLayoutConfig";
import {
  clearLayoutCache,
  getBundledLayout,
  getDashboardLayout,
  preloadDashboardLayout,
} from "../services/uiLayoutService";
import { logLayoutEvent } from "../utils/uiLayoutLogger";
import { useUser } from "../pages/login/UserContextPersistent";

const LayoutContext = createContext(null);

const emptyEntry = () => ({
  layout: null,
  source: null,
  errors: [],
  loading: false,
});

export function LayoutProvider({ children }) {
  const { state: userState } = useUser();
  const organizationId = userState?.user?.organizationId;
  const [layoutsByPageId, setLayoutsByPageId] = useState({});

  const setPageEntry = useCallback((pageId, patch) => {
    setLayoutsByPageId((prev) => ({
      ...prev,
      [pageId]: { ...emptyEntry(), ...prev[pageId], ...patch },
    }));
  }, []);

  const preloadLayout = useCallback(
    async (pageId, orgId = organizationId) => {
      if (!pageId || !orgId) return emptyEntry();

      const bundledLayout = getBundledLayout(pageId);
      setLayoutsByPageId((prev) => {
        const current = prev[pageId] || emptyEntry();
        if (current.layout) return prev;
        if (!bundledLayout) {
          return { ...prev, [pageId]: { ...current, loading: true } };
        }
        return {
          ...prev,
          [pageId]: {
            layout: bundledLayout,
            source: "fallback",
            errors: [],
            loading: true,
          },
        };
      });

      try {
        const result = await preloadDashboardLayout({ organizationId: orgId, pageId });
        logLayoutEvent("layout_preloaded", { pageId, source: result.source, orgId });
        setPageEntry(pageId, {
          layout: result.layout,
          source: result.source,
          errors: result.errors || [],
          loading: false,
        });
        return result;
      } catch (error) {
        setPageEntry(pageId, { loading: false, errors: [String(error?.message || error)] });
        return emptyEntry();
      }
    },
    [organizationId, setPageEntry],
  );

  const preloadForSelectedApp = useCallback(
    async (selectedApp, orgId = organizationId) => {
      const pageId = getPageIdForSelectedApp(selectedApp);
      if (!pageId) return null;
      return preloadLayout(pageId, orgId);
    },
    [organizationId, preloadLayout],
  );

  const invalidateLayout = useCallback(
    (pageId, orgId = organizationId) => {
      if (!pageId || !orgId) return;
      clearLayoutCache(orgId, pageId);
      setLayoutsByPageId((prev) => {
        const next = { ...prev };
        delete next[pageId];
        return next;
      });
      logLayoutEvent("layout_cache_cleared", { pageId, orgId });
    },
    [organizationId],
  );

  const getLayoutEntry = useCallback(
    (pageId) => layoutsByPageId[pageId] || emptyEntry(),
    [layoutsByPageId],
  );

  const value = useMemo(
    () => ({
      organizationId,
      preloadLayout,
      preloadForSelectedApp,
      invalidateLayout,
      getLayoutEntry,
    }),
    [organizationId, preloadLayout, preloadForSelectedApp, invalidateLayout, getLayoutEntry],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayoutContext() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayoutContext must be used within LayoutProvider");
  }
  return context;
}

export function usePageLayout(pageId) {
  const { organizationId, getLayoutEntry, preloadLayout } = useLayoutContext();
  const entry = pageId ? getLayoutEntry(pageId) : emptyEntry();
  const bundledLayout = pageId ? getBundledLayout(pageId) : null;
  const layout = entry.layout ?? bundledLayout;

  React.useEffect(() => {
    if (!pageId || !organizationId) return;
    if (!entry.layout && !entry.loading) {
      preloadLayout(pageId, organizationId);
    }
  }, [pageId, organizationId, entry.layout, entry.loading, preloadLayout]);

  return {
    layout,
    loading: false,
    refreshing: Boolean(entry.loading && layout),
    errors: entry.errors,
    source: entry.source ?? (bundledLayout && !entry.layout ? "fallback" : entry.source),
  };
}

export const useDashboardLayout = usePageLayout;
